import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboardStats } from '../../Redux/adminSlice';
import ContentLoader from '../utils/ContentLoader';
import { 
  FiUsers, 
  FiBriefcase,  
  FiUserX, 
  FiCheckCircle, 
  FiAlertCircle
} from 'react-icons/fi';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid, 
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#F87171', '#60A5FA'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Dashboard = () => {
  const dispatch = useDispatch();
  const { stats, loading, error } = useSelector((state) => state.admin.dashboard);
  
  useEffect(() => {
    dispatch(fetchDashboardStats());
  }, [dispatch]);
  
  // Format growth data for charts
  const formatGrowthData = (growthData) => {
    return growthData.map(item => ({
      month: MONTHS[item._id.month - 1],
      year: item._id.year,
      count: item.count,
      name: `${MONTHS[item._id.month - 1]} ${item._id.year}`
    }));
  };
  
  const userGrowthData = stats?.growth?.users ? formatGrowthData(stats.growth.users) : [];
  const companyGrowthData = stats?.growth?.companies ? formatGrowthData(stats.growth.companies) : [];
  
  // Format content type distribution for pie chart
  const contentTypeData = stats?.contentTypeDistribution || [];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <button 
          onClick={() => dispatch(fetchDashboardStats())}
          className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
        >
          Refresh Data
        </button>
      </div>

      {/* Show loader ONLY when refreshing / fetching */}
      {loading && <ContentLoader message="Refreshing dashboard..." />}

      {/* Error UI */}
      {!loading && error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">
          <h3 className="text-lg font-medium">Error loading dashboard data</h3>
          <p>{error}</p>
          <button
            onClick={() => dispatch(fetchDashboardStats())}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Page Content */}
      {!loading && !error && stats && (
        <>
          {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Influencers" 
          value={stats.users.total} 
          icon={<FiUsers className="w-10 h-10" />}
          color="bg-blue-500"
        />
        <StatCard 
          title="Active Influencers" 
          value={stats.users.active} 
          icon={<FiCheckCircle className="w-10 h-10" />}
          color="bg-green-500"
        />
        <StatCard 
          title="Incomplete Profiles" 
          value={stats.users.incomplete} 
          icon={<FiUserX className="w-10 h-10" />}
          color="bg-yellow-500"
        />
        <StatCard 
          title="Total Companies" 
          value={stats.companies.total} 
          icon={<FiBriefcase className="w-10 h-10" />}
          color="bg-purple-500"
        />
      </div>
      
      {/* Additional stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard 
          title="Verified Companies" 
          value={`${stats.companies.verified} of ${stats.companies.total}`} 
          icon={<FiCheckCircle className="w-10 h-10" />}
          color="bg-green-500"
          percentage={Math.round((stats.companies.verified / (stats.companies.total || 1)) * 100)}
        />
        <StatCard 
          title="Unverified Companies" 
          value={`${stats.companies.total - stats.companies.verified} of ${stats.companies.total}`} 
          icon={<FiAlertCircle className="w-10 h-10" />}
          color="bg-red-500"
          percentage={Math.round(((stats.companies.total - stats.companies.verified) / (stats.companies.total || 1)) * 100)}
        />
      </div>
      
      {/* Growth charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold text-gray-800 mb-6">User Growth (Last 6 Months)</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} users`, 'Count']}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Bar dataKey="count" name="New Users" fill="#60A5FA" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold text-gray-800 mb-6">Company Growth (Last 6 Months)</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={companyGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} companies`, 'Count']}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Bar dataKey="count" name="New Companies" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Content type distribution chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-bold text-gray-800 mb-6">Content Type Distribution</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={contentTypeData}
                dataKey="count"
                nameKey="_id"
                cx="50%"
                cy="50%"
                outerRadius={120}
                fill="#8884d8"
                label={({ _id, count, percent }) => `${_id}: ${count} (${(percent * 100).toFixed(0)}%)`}
              >
                {contentTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [`${value} users`, props.payload._id]}
              />
              <Legend 
                formatter={(value, entry, index) => entry.payload._id}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Recent users and companies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Recent Influencers</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Username</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Email</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Joined</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.recentUsers && stats.recentUsers.map((user, index) => (
                  <tr key={user._id || index}>
                    <td className="py-3 px-4 text-gray-800">{user.username}</td>
                    <td className="py-3 px-4 text-gray-600">{user.email}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                        ${user.status === 'active' ? 'bg-green-100 text-green-800' : 
                          user.status === 'blocked' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'}`}
                      >
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Recent Companies</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Company</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Email</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Joined</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Verified</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.recentCompanies && stats.recentCompanies.map((company, index) => (
                  <tr key={company._id || index}>
                    <td className="py-3 px-4 text-gray-800">{company.companyName}</td>
                    <td className="py-3 px-4 text-gray-600">{company.email}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                        ${company.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                      >
                        {company.isVerified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
        </>
      )}

      {/* No data state */}
      {!loading && !error && !stats && (
        <div className="text-center py-12">
          <p className="text-gray-500">No dashboard data available yet.</p>
          <button
            onClick={() => dispatch(fetchDashboardStats())}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, percentage }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center">
        <div className={`${color} p-3 rounded-md text-white`}>
          {icon}
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <div className="flex items-center">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {percentage !== undefined && (
              <span className="ml-2 text-sm font-medium text-gray-500">
                ({percentage}%)
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 